import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { DepositAmountPage } from '@pages/Traders-Room/Deposit/Amount/DepositAmount.page'
import { DepositCardDetailsPage } from '@pages/Traders-Room/Deposit/CardDetails/DepositCardDetails.page'
import { DepositPage } from '@pages/Traders-Room/Deposit/Deposit.page'
import { DepositFlowLayoutPage } from '@pages/Traders-Room/Deposit/DepositFlowLayout/DepositFlowLayout.page'
import { DepositRedirectPage } from '@pages/Traders-Room/Deposit/Redirect/DepositRedirect.page'
import { DepositTransactionPage } from '@pages/Traders-Room/Deposit/Transaction/DepositTransaction.page'
import { WithdrawalAmountPage } from '@pages/Traders-Room/Withdrawal/Amount/WithdrawalAmount.page'
import { WithdrawalTransactionPage } from '@pages/Traders-Room/Withdrawal/Transaction/WithdrawalTransaction.page'
import { WithdrawalTwoFactorPage } from '@pages/Traders-Room/Withdrawal/TwoFactor/WithdrawalTwoFactor.page'
import { WithdrawalPage } from '@pages/Traders-Room/Withdrawal/Withdrawal.page'
import { WithdrawalFlowLayoutPage } from '@pages/Traders-Room/Withdrawal/WithdrawalFlowLayout/WithdrawalFlowLayout.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { DateFilterContextProvider } from '@context/DateFilterContext'

import { PaymentAgent } from './PaymentAgent'
import { ProfilePage } from './Profile/ProfilePage'
import { PaymentAgentTransactionHistoryPage } from './TransactionHistory/PaymentAgentTransactionHistoryPage'
import { PaymentAgentWalletTransferPage } from './Wallets/PaymentAgentTransfer/PaymentAgentWalletTransferPage'
import { PaymentAgentWalletsPage } from './Wallets/PaymentAgentWalletsPage'

const RedirectToSelection: React.FC<{ target: 'wallet' | 'trading-account' }> = ({ target }) => {
  const { walletId } = useParams<{ walletId?: string }>()
  const location = useLocation()
  return (
    <Navigate
      to={`/dashboard/payment-agent/deposit/${target}`}
      state={{
        ...location.state,
        selectedWalletId: walletId || location.state?.selectedWalletId,
      }}
      replace
    />
  )
}

const RedirectToWithdrawal: React.FC = () => {
  const { walletId } = useParams<{ walletId?: string }>()
  const location = useLocation()
  return (
    <Navigate
      to='/dashboard/payment-agent/withdrawal/wallet'
      state={{ ...location.state, selectedWalletId: walletId || location.state?.selectedWalletId }}
      replace
    />
  )
}

const RedirectToDeposit: React.FC<{ target: 'wallet' | 'trading-account' }> = ({ target }) => {
  const { walletId } = useParams<{ walletId?: string }>()
  const location = useLocation()
  return (
    <Navigate
      to={`/dashboard/payment-agent/deposit/${target}`}
      state={{
        ...location.state,
        selectedWalletId: walletId,
      }}
      replace
    />
  )
}

export const usePARoutes = (): RouteConfig[] => {
  const visible = usePARoutesConditions()
  const { t } = useTranslation()

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'payment-agent',
        outlet: <PaymentAgent />,
        condition: visible.isPAVisible,
        meta: { breadcrumbLabel: t('Tabs.Payment Agent') },
        children: [
          {
            path: 'profile-info',
            element: <ProfilePage />,
            meta: { breadcrumbLabel: t('Tabs.Profile Info') },
          },
          {
            path: 'wallets',
            element: <PaymentAgentWalletsPage />,
            meta: { breadcrumbLabel: t('Tabs.Wallets'), hideChildrenBreadcrumbs: true },
            children: [
              {
                path: 'deposit',
                element: <RedirectToSelection target='wallet' />,
              },
              {
                path: ':walletId',
                children: [
                  {
                    path: 'withdrawal',
                    element: <RedirectToWithdrawal />,
                  },
                  {
                    path: 'deposit',
                    element: <RedirectToDeposit target='wallet' />,
                  },
                  {
                    path: 'transfer',
                    element: <PaymentAgentWalletTransferPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'deposit',
            element: <RedirectToSelection target='wallet' />,
            layout: 'full-no-menu',
            children: [
              {
                path: ':target',
                element: <DepositPage dashboardContext='payment-agent' />,
              },
              {
                path: 'wallet/:walletId/:pspId',
                outlet: <DepositFlowLayoutPage target='wallet' dashboardContext='payment-agent' />,
                element: <Navigate to='amount' replace />,
                children: [
                  { path: 'amount', element: <DepositAmountPage target='wallet' /> },
                  { path: 'card-details', element: <DepositCardDetailsPage /> },
                  { path: 'redirect', element: <DepositRedirectPage /> },
                  { path: 'transaction/:status', element: <DepositTransactionPage /> },
                ],
              },
            ],
          },
          {
            path: 'withdrawal',
            element: <RedirectToWithdrawal />,
            layout: 'full-no-menu',
            children: [
              {
                path: 'wallet',
                element: <WithdrawalPage dashboardContext='payment-agent' />,
              },
              {
                path: 'wallet/:id/:pspId',
                outlet: (
                  <WithdrawalFlowLayoutPage target='wallet' dashboardContext='payment-agent' />
                ),
                element: <Navigate to='amount' replace />,
                children: [
                  { path: 'amount', element: <WithdrawalAmountPage /> },
                  { path: 'two-factor', element: <WithdrawalTwoFactorPage /> },
                  { path: 'transaction/:status', element: <WithdrawalTransactionPage /> },
                ],
              },
            ],
          },
          {
            path: 'transaction-history',
            element: (
              <DateFilterContextProvider>
                <PaymentAgentTransactionHistoryPage />
              </DateFilterContextProvider>
            ),
            meta: { breadcrumbLabel: t('Tabs.Transaction History') },
          },
        ],
      },
    ],
    [t, visible.isPAVisible]
  )
}

interface RoutesConditions {
  isPAVisible: boolean
}

export const usePARoutesConditions = (): RoutesConditions => {
  const { account } = useAccountReadContext()

  const isPAVisible = useMemo(
    () =>
      !!account?.clientPaymentAgent &&
      account?.clientPaymentAgent.isActive &&
      account?.visibilityConfiguration.hasPARoom,
    [account]
  )

  return {
    isPAVisible,
  }
}
